import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Tag, Spin, Modal, message, Statistic, Drawer, List, Row, Col, Select, Space, Alert } from 'antd';
import { 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined,
  PlusOutlined, 
  ReloadOutlined, 
  BarChartOutlined, 
  DownloadOutlined,
  FilterOutlined,
  CloseOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { Expense, ExpenseFilters, ExpenseStats } from '../../types/expense';
import { expenseService } from '../../services/expenseService';
import ExpenseDetailModalMobile from '../../components/expenses/ExpenseDetailModalMobile';
import { DatePickerModal } from '../../components/common/DatePickerModal';
import { formatCurrency, formatDate } from '../../utils/formatters';
import styles from './ExpensesPageMobile.module.css';

const { Option } = Select;

const ExpensesPageMobile: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [stats, setStats] = useState<ExpenseStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | undefined>();
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<ExpenseFilters>({
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().endOf('month').format('YYYY-MM-DD')
  });
  const [showFilters, setShowFilters] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerExpense, setDrawerExpense] = useState<Expense | null>(null);
  const [datePickerState, setDatePickerState] = useState<{
    isOpen: boolean;
    currentDate: Dayjs | null;
    field: 'date_from' | 'date_to' | null;
  }>({
    isOpen: false,
    currentDate: null,
    field: null
  });

  useEffect(() => {
    loadExpenses();
    loadStats();
  }, [filters]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const data = await expenseService.getExpenses(filters);
      setExpenses(data);
    } catch (error: any) {
      setError('Lỗi khi tải danh sách chi phí');
      console.error('Lỗi:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await expenseService.getExpenseStats();
      setStats(data);
    } catch (error) {
      console.error('Lỗi khi tải thống kê:', error);
    }
  };

  const handleSave = () => {
    loadExpenses();
    loadStats();
  };

  const handleEdit = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowModal(true);
  };

  const handleDelete = async (expense: Expense) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa chi phí "${expense.title}" (${expense.expense_number})?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await expenseService.deleteExpense(expense.id);
          message.success('Xóa chi phí thành công');
          loadExpenses();
          loadStats();
        } catch (error: any) {
          message.error('Lỗi khi xóa chi phí: ' + (error.response?.data?.message || error.message));
        }
      },
    });
  };

  const handleView = (expense: Expense) => {
    setDrawerExpense(expense);
    setDrawerVisible(true);
  };

  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [name]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({
      date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
      date_to: dayjs().endOf('month').format('YYYY-MM-DD')
    });
  };

  const openDatePicker = (field: 'date_from' | 'date_to', currentDate?: string) => {
    setDatePickerState({
      isOpen: true,
      currentDate: currentDate ? dayjs(currentDate) : null,
      field
    });
  };

  const closeDatePicker = () => {
    setDatePickerState({
      isOpen: false,
      currentDate: null,
      field: null
    });
  };

  const handleDateSelect = (date: Dayjs) => {
    if (datePickerState.field) {
      handleFilterChange(datePickerState.field, date.format('YYYY-MM-DD'));
    }
    closeDatePicker();
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'paid':
        return <Tag color="success">Đã thanh toán</Tag>;
      case 'pending':
        return <Tag color="warning">Chờ thanh toán</Tag>;
      case 'cancelled':
        return <Tag color="error">Đã hủy</Tag>;
      default:
        return <Tag color="default">Không xác định</Tag>;
    }
  };

  const getPaymentMethodText = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Tiền mặt';
      case 'bank_transfer':
        return 'Chuyển khoản';
      case 'credit_card':
        return 'Thẻ tín dụng';
      case 'other':
        return 'Khác';
      default:
        return method;
    }
  };

  return (
    <div className={styles['expenses-mobile-container']}>
      {/* ===== HEADER ===== */}
      <header className={styles['expenses-mobile-header']}>
        

        {/* Thanh tìm kiếm và bộ lọc */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Button
              style={{ flexGrow: 1, padding: 8, fontSize: 14, borderRadius: 6 }}
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Bộ lọc
            </Button>
            <Button
              style={{ padding: 8, fontSize: 14, borderRadius: 6 }}
              icon={<ReloadOutlined />}
              onClick={() => {
                loadExpenses();
                loadStats();
              }}
            />
          </div>
          
          {/* Bộ lọc */}
          {showFilters && (
            <Card size="small" style={{ marginBottom: 8 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Từ ngày</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Input 
                      placeholder="Chọn ngày" 
                      value={filters.date_from ? dayjs(filters.date_from).format('DD/MM/YYYY') : ''}
                      readOnly
                      style={{ flex: 1, fontSize: 12 }}
                    />
                    <Button 
                      type="primary" 
                      icon={<CalendarOutlined />} 
                      onClick={() => openDatePicker('date_from', filters.date_from)}
                      style={{ padding: '4px 8px', fontSize: 12 }}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Đến ngày</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Input 
                      placeholder="Chọn ngày" 
                      value={filters.date_to ? dayjs(filters.date_to).format('DD/MM/YYYY') : ''}
                      readOnly
                      style={{ flex: 1, fontSize: 12 }}
                    />
                    <Button 
                      type="primary" 
                      icon={<CalendarOutlined />} 
                      onClick={() => openDatePicker('date_to', filters.date_to)}
                      style={{ padding: '4px 8px', fontSize: 12 }}
                    />
                  </div>
                </Col>
              </Row>
              <Row gutter={8} style={{ marginTop: 8 }}>
                <Col span={24}>
                  <Select
                    placeholder="Trạng thái"
                    style={{ width: '100%', fontSize: 12 }}
                    value={filters.payment_status}
                    onChange={(value) => handleFilterChange('payment_status', value)}
                    allowClear
                  >
                    <Option value="pending">Chờ thanh toán</Option>
                    <Option value="paid">Đã thanh toán</Option>
                    <Option value="cancelled">Đã hủy</Option>
                  </Select>
                </Col>
              </Row>
            </Card>
          )}
        </div>


      </header>

      {/* ===== DANH SÁCH CHI PHÍ ===== */}
      <Spin spinning={loading}>
        <div className={styles['expenses-mobile-list']}>
          {error && (
            <Alert 
              message={error} 
              type="error" 
              style={{ marginBottom: 16 }} 
              showIcon
            />
          )}
          
          {expenses.map((expense: Expense) => (
            <div
              key={expense.id}
              className={styles['expenses-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 8px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#1890ff', textAlign: 'left', marginBottom: 4 }}>
                      {expense.expense_number}
                    </div>
                    <div style={{ fontSize: 15, color: '#222', textAlign: 'left', fontWeight: 500, marginBottom: 4 }}>
                      {expense.title}
                    </div>
                    {expense.description && (
                      <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'left', fontStyle: 'italic' }}>
                        {expense.description}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: 12 }}>
                    <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                      {formatDate(expense.expense_date)}
                    </span>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280' }}>Danh mục:</span>
                    <span style={{ fontWeight: 500, color: '#222' }}>
                      {expense.category_name || 'Không xác định'}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280' }}>Số tiền:</span>
                    <span style={{ fontWeight: 700, color: '#ff4d4f', fontSize: 16 }}>
                      {formatCurrency(expense.amount)}
                    </span>
                  </div>
                  
                  {expense.supplier_name && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#6b7280' }}>Nhà cung cấp:</span>
                      <span style={{ fontWeight: 500, color: '#222' }}>
                        {expense.supplier_name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ background: '#f9fafb', padding: '12px 20px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <button 
                    onClick={() => handleView(expense)} 
                    className={styles['expenses-mobile-detail-btn']} 
                    style={{ 
                      padding: 8, 
                      borderRadius: '50%', 
                      color: '#1890ff', 
                      background: 'none', 
                      border: 'none', 
                      fontSize: 18, 
                      transition: 'all 0.2s' 
                    }}
                    title="Xem chi tiết"
                  >
                    <EyeOutlined />
                  </button>
                  <button 
                    onClick={() => handleEdit(expense)} 
                    className={styles['expenses-mobile-detail-btn']} 
                    style={{ 
                      padding: 8, 
                      borderRadius: '50%', 
                      color: '#faad14', 
                      background: 'none', 
                      border: 'none', 
                      fontSize: 18, 
                      transition: 'all 0.2s' 
                    }}
                    title="Chỉnh sửa"
                  >
                    <EditOutlined />
                  </button>
                  <button 
                    onClick={() => handleDelete(expense)} 
                    className={styles['expenses-mobile-detail-btn']} 
                    style={{ 
                      padding: 8, 
                      borderRadius: '50%', 
                      color: '#ff4d4f', 
                      background: 'none', 
                      border: 'none', 
                      fontSize: 18, 
                      transition: 'all 0.2s' 
                    }}
                    title="Xóa"
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {expenses.length === 0 && !loading && (
            <div className={styles['expenses-mobile-empty']}>
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{ fontSize: 16, marginBottom: 8 }}>Không có chi phí nào được tìm thấy</div>
                <div style={{ fontSize: 14, color: '#999' }}>Hãy thử thay đổi bộ lọc hoặc tạo chi phí mới</div>
              </div>
            </div>
          )}
        </div>
      </Spin>

      {/* ===== DRAWER XEM CHI TIẾT ===== */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Chi tiết chi phí
          </div>
        }
        placement="bottom"
        height="90vh"
        onClose={() => {
          setDrawerVisible(false);
          setDrawerExpense(null);
        }}
        open={drawerVisible}
        className={styles['expenses-mobile-drawer']}
      >
        {drawerExpense ? (
          <div>
            <Card size="small" style={{ marginBottom: 12 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Statistic
                    title="Mã chi phí"
                    value={drawerExpense.expense_number}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Số tiền"
                    value={drawerExpense.amount}
                    suffix="VNĐ"
                    valueStyle={{ color: '#ff4d4f' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </Col>
              </Row>
            </Card>
            
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Thông tin cơ bản:</strong>
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Tiêu đề:</strong> {drawerExpense.title}
              </div>
              {drawerExpense.description && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Mô tả:</strong> {drawerExpense.description}
                </div>
              )}
              <div style={{ marginBottom: 4 }}>
                <strong>Danh mục:</strong> {drawerExpense.category_name || 'Không xác định'}
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Ngày chi phí:</strong> {formatDate(drawerExpense.expense_date)}
              </div>
            </Card>
            
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Thông tin thanh toán:</strong>
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Phương thức:</strong> {getPaymentMethodText(drawerExpense.payment_method)}
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Trạng thái:</strong> {getStatusTag(drawerExpense.payment_status)}
              </div>
              {drawerExpense.supplier_name && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Nhà cung cấp:</strong> {drawerExpense.supplier_name}
                </div>
              )}
              {drawerExpense.invoice_number && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Số hóa đơn:</strong> {drawerExpense.invoice_number}
                </div>
              )}
            </Card>
            
            {drawerExpense.notes && (
              <Card size="small">
                <div style={{ marginBottom: 8 }}>
                  <strong>Ghi chú:</strong>
                </div>
                <div>{drawerExpense.notes}</div>
              </Card>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <EyeOutlined style={{ fontSize: '48px', marginBottom: 16 }} />
            <div>Không tìm thấy thông tin chi tiết</div>
          </div>
        )}
      </Drawer>

      {/* ===== MODAL THÊM/SỬA CHI PHÍ ===== */}
      <ExpenseDetailModalMobile
        visible={showModal}
        onCancel={() => setShowModal(false)}
        editingExpense={selectedExpense}
        isLoadingDetail={false}
      />

      {/* ===== DATE PICKER MODAL ===== */}
      <DatePickerModal
        isOpen={datePickerState.isOpen}
        onClose={closeDatePicker}
        onSelect={handleDateSelect}
        selectedDate={datePickerState.currentDate || undefined}
        title={`Chọn ngày ${datePickerState.field === 'date_from' ? 'bắt đầu' : 'kết thúc'}`}
      />
    </div>
  );
};

export default ExpensesPageMobile;
